import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, ScrollText, ChevronLeft, ChevronRight, ArrowRight, Download, FilterX } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/ui/forms";
import { fmtDateTime } from "@/lib/datetime";

const PAGE_SIZE = 20;

const ACCION_STYLE = {
  CAMBIO_ROL: "text-[#1D4ED8] border-[#3B82F6]/40 bg-[#3B82F6]/5",
  SUSPENSION_USUARIO: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5",
  SUSPENSION_EMPRESA: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5",
  REACTIVACION_USUARIO: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5",
  REACTIVACION_EMPRESA: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5",
  CAMBIO_LIMITE_CREDITO: "text-[#7C3AED] border-[#8B5CF6]/40 bg-[#8B5CF6]/5",
};

const ACCIONES_TENANT = ["CAMBIO_ROL", "SUSPENSION_USUARIO", "REACTIVACION_USUARIO", "CAMBIO_LIMITE_CREDITO"];
const ACCIONES_ADMIN = ["SUSPENSION_EMPRESA", "REACTIVACION_EMPRESA", "CAMBIO_ROL", "SUSPENSION_USUARIO", "REACTIVACION_USUARIO", "CAMBIO_LIMITE_CREDITO"];
const ENTIDADES = ["usuario", "empresa", "contrato"];

const th = "font-display font-bold px-4 py-3 text-xs tracking-wider uppercase text-left text-[#52525B]";
const fld = "h-10 rounded-none border-[#0A0A0A]";

const fmtFecha = (iso) => fmtDateTime(iso);
const fmtValor = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(", ");
  return String(v);
};
const endOfDay = (iso) => { const d = new Date(iso); d.setHours(23, 59, 59, 999); return d.toISOString(); };

export default function Bitacora() {
  const { user } = useAuth();
  const esSuper = user?.rol === "SuperAdmin";
  const baseEndpoint = esSuper ? "/admin/auditoria" : "/tenant/auditoria";
  const accionesDisponibles = esSuper ? ACCIONES_ADMIN : ACCIONES_TENANT;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [f, setF] = useState({ accion: "all", entidad: "all", desde: "", hasta: "", tenant_id: "all" });

  const buildParams = (p, size) => {
    const params = new URLSearchParams({ page: String(p), page_size: String(size) });
    if (f.accion !== "all") params.set("accion", f.accion);
    if (f.entidad !== "all") params.set("entidad", f.entidad);
    if (f.desde) params.set("desde", f.desde);
    if (f.hasta) params.set("hasta", endOfDay(f.hasta));
    if (esSuper && f.tenant_id !== "all") params.set("tenant_id", f.tenant_id);
    return params.toString();
  };

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get(`${baseEndpoint}?${buildParams(p, PAGE_SIZE)}`);
      setItems(data.items); setTotal(data.total); setPage(data.page);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseEndpoint, f, esSuper]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    if (esSuper) api.get("/empresas").then(({ data }) => setEmpresas(data)).catch(() => {});
  }, [esSuper]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      // Trae TODAS las páginas del resultado filtrado (evita el tope de 1000).
      const rows = [];
      let p = 1;
      while (true) {
        const { data } = await api.get(`${baseEndpoint}?${buildParams(p, 1000)}`);
        rows.push(...data.items);
        if (rows.length >= data.total || data.items.length === 0) break;
        p += 1;
      }
      if (rows.length === 0) { toast.info("No hay registros para exportar"); return; }
      const headers = ["Fecha", "Entidad", "Accion", "Anterior", "Nuevo", "Afectado", "Autorizo", "Motivo", "TenantId"];
      const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      const lines = rows.map((a) => [
        fmtFecha(a.fecha_utc), a.entidad_afectada, a.accion,
        fmtValor(a.valor_anterior), fmtValor(a.valor_nuevo),
        a.afectado || "", a.autorizo || "", a.motivo || "", a.tenant_id || "",
      ].map(esc).join(","));
      const csv = [headers.map(esc).join(","), ...lines].join("\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bitacora_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${rows.length} registro(s) exportado(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const limpiar = () => setF({ accion: "all", entidad: "all", desde: "", hasta: "", tenant_id: "all" });
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="mb-6">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 flex items-center gap-2"><ScrollText className="h-4 w-4" /> Trazabilidad</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Bitácora de cambios</h1>
          <p className="text-sm text-[#52525B] mt-2">{
            esSuper
              ? (f.tenant_id !== "all"
                  ? `Auditoría del tenant: ${empresas.find((e) => e.id_empresa === f.tenant_id)?.nombre || "—"} (modo soporte).`
                  : "Eventos de nivel de plataforma. Filtra por tenant para soporte.")
              : "Registro de acciones sensibles de tu empresa: quién cambió qué y cuándo."
          }</p>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-[#e4e4e7] p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="bitacora-filtros">
          <div className="space-y-1">
            <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Acción</Label>
            <Select value={f.accion} onValueChange={(v) => setF({ ...f, accion: v })}>
              <SelectTrigger data-testid="filtro-accion" className={`${fld} w-52`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las acciones</SelectItem>
                {accionesDisponibles.map((a) => <SelectItem key={a} value={a} data-testid={`filtro-accion-${a}`}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Entidad</Label>
            <Select value={f.entidad} onValueChange={(v) => setF({ ...f, entidad: v })}>
              <SelectTrigger data-testid="filtro-entidad" className={`${fld} w-40`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ENTIDADES.map((e) => <SelectItem key={e} value={e} data-testid={`filtro-entidad-${e}`} className="capitalize">{e}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Desde</Label>
            <DateField value={f.desde} onChange={(v) => setF({ ...f, desde: v || "" })} max={f.hasta ? new Date(f.hasta).toISOString().slice(0, 10) : undefined} data-testid="filtro-desde" className={`${fld} w-44`} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Hasta</Label>
            <DateField value={f.hasta} onChange={(v) => setF({ ...f, hasta: v || "" })} data-testid="filtro-hasta" className={`${fld} w-44`} />
          </div>
          {esSuper && (
            <div className="space-y-1">
              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Tenant (soporte)</Label>
              <Select value={f.tenant_id} onValueChange={(v) => setF({ ...f, tenant_id: v })}>
                <SelectTrigger data-testid="filtro-tenant" className={`${fld} w-56`}><SelectValue placeholder="Solo plataforma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Solo plataforma</SelectItem>
                  {empresas.map((e) => <SelectItem key={e.id_empresa} value={e.id_empresa} data-testid={`filtro-tenant-${e.id_empresa}`}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={limpiar} data-testid="bitacora-limpiar" className="h-10 px-3 border border-[#e4e4e7] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-1.5 hover:border-[#0A0A0A] transition-colors duration-200"><FilterX className="h-4 w-4" /> Limpiar</button>
            <button onClick={exportCSV} disabled={exporting} data-testid="bitacora-export-csv" className="btn-hard h-10 px-4 bg-[#0A0A0A] text-white border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-60">{exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Exportar CSV</button>
          </div>
        </div>

        <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
          <table className="w-full text-sm" data-testid="bitacora-table">
            <thead>
              <tr className="border-b border-[#e4e4e7]">
                <th className={th}>Fecha</th><th className={th}>Entidad</th><th className={th}>Acción</th>
                <th className={th}>Cambio</th><th className={th}>Afectado</th><th className={th}>Autorizó</th><th className={th}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-[#52525B]" data-testid="bitacora-empty">Sin registros con los filtros actuales.</td></tr>
              ) : items.map((a) => (
                <tr key={a.id} className="border-b border-[#f0f0f1]" data-testid={`bitacora-row-${a.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtFecha(a.fecha_utc)}</td>
                  <td className="px-4 py-3 capitalize">{a.entidad_afectada}</td>
                  <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ACCION_STYLE[a.accion] || "text-[#52525B] border-[#A1A1AA]/40 bg-[#A1A1AA]/5"}`}>{a.accion}</span></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-2 font-mono text-xs"><span className="text-[#B45309]">{fmtValor(a.valor_anterior)}</span><ArrowRight className="h-3 w-3 text-[#52525B]" /><span className="text-[#047857] font-semibold">{fmtValor(a.valor_nuevo)}</span></span></td>
                  <td className="px-4 py-3">{a.afectado || "—"}</td>
                  <td className="px-4 py-3">{a.autorizo || "—"}</td>
                  <td className="px-4 py-3 text-[#52525B]">{a.motivo || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#52525B]" data-testid="bitacora-total">{total} registro(s) · Página {page} de {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => load(page - 1)} disabled={page <= 1 || loading} data-testid="bitacora-prev" className="h-9 px-3 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs flex items-center gap-1 disabled:opacity-40 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200"><ChevronLeft className="h-4 w-4" /> Anterior</button>
            <button onClick={() => load(page + 1)} disabled={page >= totalPages || loading} data-testid="bitacora-next" className="h-9 px-3 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs flex items-center gap-1 disabled:opacity-40 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">Siguiente <ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </motion.div>
    </DashboardLayout>
  );
}
